import { Badge } from '@/components/UI';
import SummaryStat from './SummaryStat';
import { formatTimestamp, getAiIssueTriageRunBadgeVariant } from './helpers';
import type { AiCandidateCurationRunSnapshot } from './types';

interface AiCandidateCurationRunSectionProps {
    snapshot: AiCandidateCurationRunSnapshot | undefined;
    isLoading: boolean;
    errorMessage: string | undefined;
}

const AiCandidateCurationRunSection = ({ snapshot, isLoading, errorMessage }: AiCandidateCurationRunSectionProps) => {
    if (isLoading) return <div className="text-sm text-zinc-500">Loading AI curation status...</div>;
    if (errorMessage) return <div className="rounded border border-red-900/70 bg-red-950/20 p-3 text-sm text-red-300">{errorMessage}</div>;
    if (!snapshot) return null;

    const { run, queue } = snapshot;
    const status = run?.status ?? 'not_started';
    const statusTone = getAiIssueTriageRunBadgeVariant(status);

    return (
        <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3">
            <div className="mb-3 flex items-center justify-between gap-2">
                <div className="font-medium text-white">AI Candidate Curation Run</div>
                <Badge variant={statusTone}>{status}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5 lg:grid-cols-10">
                <SummaryStat
                    label="Uncurated"
                    tone={queue.uncurated > 0 && status !== 'completed' ? 'warn' : 'good'}
                    value={queue.uncurated}
                />
                <SummaryStat label="Selected" value={run?.featuresSelected ?? 0} />
                <SummaryStat label="Reviewed" value={run?.featuresReviewed ?? 0} />
                <SummaryStat label="Written" value={run?.reviewsWritten ?? 0} />
                <SummaryStat label="Preferred" value={run?.preferredCount ?? 0} />
                <SummaryStat label="Valuable" value={run?.valuableCount ?? 0} />
                <SummaryStat label="Maybe" value={run?.maybeCount ?? 0} />
                <SummaryStat label="Suspect" value={run?.suspectCount ?? 0} />
                <SummaryStat label="Broken" tone={(run?.brokenCount ?? 0) > 0 ? 'bad' : 'neutral'} value={run?.brokenCount ?? 0} />
                <div>
                    <div className="text-xs text-zinc-500">Heartbeat</div>
                    <div className="text-lg font-semibold text-white">{formatTimestamp(run?.lastHeartbeatAt)}</div>
                </div>
            </div>
            {run?.lastError && (
                <div className="mt-3 rounded border border-red-900/70 bg-red-950/20 p-2 text-sm text-red-300">{run.lastError}</div>
            )}
        </div>
    );
};

export default AiCandidateCurationRunSection;
